import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Heart, 
  Users, 
  Star, 
  Award, 
  Music, 
  BookOpen, 
  ArrowRight,
  DollarSign,
  Crown,
  Shield,
  Sparkles,
  Target,
  Calendar,
  Gift,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { donationService } from "@/services/donationService";
import { StripePaymentForm } from "@/components/payment/StripePaymentForm";
import type { DonationType, DonorLevel } from "@/types/donation";

const Donate = () => {
  const [donationAmount, setDonationAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [donationType, setDonationType] = useState<DonationType>("one-time");
  const [donorInfo, setDonorInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    message: "",
    anonymous: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [donationId, setDonationId] = useState<number | null>(null);

  const presetAmounts = [
    { amount: "25", impact: "Supports one student workshop" },
    { amount: "50", impact: "Funds community event supplies" },
    { amount: "100", impact: "Sponsors heritage preservation project" },
    { amount: "250", impact: "Supports monthly community program" },
    { amount: "500", impact: "Funds educational equipment purchase" },
    { amount: "1000", impact: "Sponsors annual community celebration" }
  ];

  const donorLevels = [
    {
      name: "Supporter",
      min: 25,
      max: 99,
      icon: Heart,
      color: "text-heritage",
      benefits: ["Recognition in newsletter", "Annual thank you card"]
    },
    {
      name: "Community Champion",
      min: 100,
      max: 249,
      icon: Star,
      color: "text-accent",
      benefits: ["All Supporter benefits", "Name on donor wall", "Invitation to appreciation event"]
    },
    {
      name: "Community Leader",
      min: 250,
      max: 499,
      icon: Award,
      color: "text-culture",
      benefits: ["All Community Champion benefits", "VIP event seating", "Quarterly impact reports"]
    },
    {
      name: "Guardian",
      min: 500,
      max: 999,
      icon: Shield,
      color: "text-primary",
      benefits: ["All Community Leader benefits", "Annual facility tour", "Meet with leadership team"]
    },
    {
      name: "Legacy Patron",
      min: 1000,
      max: Infinity,
      icon: Crown,
      color: "text-heritage",
      benefits: ["All Guardian benefits", "Permanent recognition plaque", "Annual legacy dinner invitation"]
    }
  ];

  const impactAreas = [
    {
      title: "Preservation",
      description: "Digital archiving, oral history projects, and artifact preservation",
      icon: BookOpen,
      goal: 15000,
      raised: 8500
    },
    {
      title: "Youth Programs",
      description: "Student performing arts cohort, educational workshops, and mentorship programs",
      icon: Users,
      goal: 20000,
      raised: 12300
    },
    {
      title: "Community Events",
      description: "Celebrations, festivals, and community gatherings",
      icon: Calendar,
      goal: 10000,
      raised: 7800
    },
    {
      title: "Facility Improvements",
      description: "Theater upgrades, library expansion, and accessibility enhancements",
      icon: Target,
      goal: 50000,
      raised: 23400
    }
  ];

  const handleAmountSelect = (amount: string) => {
    setDonationAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setDonationAmount("");
  };

  const getCurrentAmount = () => {
    return donationAmount ? parseInt(donationAmount) : (customAmount ? parseInt(customAmount) : 0);
  };

  const getDonorLevel = (amount: number) => {
    return donorLevels.find(level => amount >= level.min && amount <= level.max) || donorLevels[0];
  };

  const currentLevel = getDonorLevel(getCurrentAmount());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!donorInfo.firstName || !donorInfo.lastName || !donorInfo.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    const amount = getCurrentAmount();
    if (amount === 0) {
      toast.error("Please select or enter a donation amount");
      return;
    }

    setIsSubmitting(true);

    try {
      const donorLevel = getDonorLevel(amount)?.name as DonorLevel | undefined;

      // First, create the donation record (with pending payment status)
      const result = await donationService.submitDonation({
        firstName: donorInfo.firstName.trim(),
        lastName: donorInfo.lastName.trim(),
        email: donorInfo.email.trim(),
        phone: donorInfo.phone?.trim() || undefined,
        address: donorInfo.address?.trim() || undefined,
        donationAmount: amount,
        donationType: donationType,
        donorLevel: donorLevel,
        message: donorInfo.message?.trim() || undefined,
        anonymous: donorInfo.anonymous,
      });

      if (result.success && result.data) {
        // Store donation ID and show payment form
        setDonationId(result.data.id);
        setShowPaymentForm(true);
        toast.success("Please complete your payment to finalize your donation.");
      } else {
        toast.error(result.error?.getUserMessage() || "Failed to submit donation. Please try again.");
      }
    } catch (error) {
      console.error('Error submitting donation:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentId: string) => {
    if (donationId) {
      // Update donation payment status
      const updateResult = await donationService.updatePaymentStatus(donationId, 'completed');
      if (updateResult.success) {
        setIsSubmitted(true);
        setShowPaymentForm(false);
        toast.success("Thank you for your donation! Payment processed successfully.");
        
        // Reset form after delay
        setTimeout(() => {
          setDonationAmount("");
          setCustomAmount("");
          setDonationType("one-time");
          setDonorInfo({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            address: "",
            message: "",
            anonymous: false
          });
          setIsSubmitted(false);
          setDonationId(null);
        }, 5000);
      }
    }
  };

  const handlePaymentError = (error: string) => {
    toast.error(error || "Payment failed. Please try again.");
  };

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Support Our <span className="text-heritage">Mission</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Your generous donation helps preserve our heritage, celebrate diversity, 
            and strengthen our community bonds for future generations in Wimauma and Tampa Bay.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center text-heritage">
            <div className="flex items-center">
              <Heart className="w-5 h-5 mr-2" />
              <span className="font-medium">100% Tax Deductible</span>
            </div>
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              <span className="font-medium">Secure Donation Platform</span>
            </div>
            <div className="flex items-center">
              <Award className="w-5 h-5 mr-2" />
              <span className="font-medium">Direct Community Impact</span>
            </div>
          </div>
        </div>
      </section>

      {/* Current Impact Goals */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Current Impact Goals
            </h2>
            <p className="text-lg text-muted-foreground">
              See how your donation directly supports our community initiatives
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {impactAreas.map((area, index) => (
              <Card key={index} className="shadow-warm hover:shadow-heritage transition-shadow">
                <CardHeader>
                  <div className="flex items-center mb-4">
                    <area.icon className="w-8 h-8 text-heritage mr-3" />
                    <CardTitle className="text-xl">{area.title}</CardTitle>
                  </div>
                  <p className="text-muted-foreground">{area.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="text-heritage font-medium">
                        ${area.raised.toLocaleString()} / ${area.goal.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-3">
                      <div 
                        className="bg-gradient-heritage h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(area.raised / area.goal) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-heritage font-medium">
                      {Math.round((area.raised / area.goal) * 100)}% Complete
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-heritage">
            <CardHeader>
              <CardTitle className="text-2xl text-center flex items-center justify-center">
                <Heart className="w-6 h-6 mr-2 text-heritage" />
                Make Your Donation
              </CardTitle>
              <p className="text-center text-muted-foreground">
                Every contribution makes a meaningful difference in our community
              </p>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Donation Amount */}
                <div>
                  <Label className="text-lg font-medium mb-6 block">Select Donation Amount</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    {presetAmounts.map((preset) => (
                      <Card 
                        key={preset.amount}
                        className={`cursor-pointer transition-all hover:shadow-heritage ${
                          donationAmount === preset.amount 
                            ? 'ring-2 ring-heritage shadow-heritage bg-heritage/5' 
                            : 'hover:border-heritage/50'
                        }`}
                        onClick={() => handleAmountSelect(preset.amount)}
                      >
                        <CardContent className="p-4 text-center">
                          <div className="text-2xl font-bold text-heritage mb-2">
                            ${preset.amount}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {preset.impact}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  <div className="mb-6">
                    <Label htmlFor="customAmount">Custom Amount</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        id="customAmount"
                        type="number"
                        placeholder="Enter custom amount"
                        value={customAmount}
                        onChange={(e) => handleCustomAmount(e.target.value)}
                        className="pl-10"
                        min="1"
                      />
                    </div>
                  </div>

                  {/* Donor Level Display */}
                  {getCurrentAmount() > 0 && (
                    <Card className="bg-heritage/5 border-heritage/20">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <currentLevel.icon className={`w-6 h-6 mr-3 ${currentLevel.color}`} />
                            <div>
                              <div className="font-medium text-foreground">{currentLevel.name}</div>
                              <div className="text-sm text-muted-foreground">Recognition Level</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-heritage">
                              ${getCurrentAmount()}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="text-sm font-medium text-foreground mb-2">Benefits Include:</div>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {currentLevel.benefits.map((benefit, index) => (
                              <li key={index} className="flex items-center">
                                <Sparkles className="w-3 h-3 mr-2 text-heritage" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Donation Type */}
                <div>
                  <Label className="text-lg font-medium mb-4 block">Donation Frequency</Label>
                  <RadioGroup 
                    value={donationType} 
                    onValueChange={setDonationType}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-secondary/50">
                      <RadioGroupItem value="one-time" id="one-time" />
                      <Label htmlFor="one-time" className="flex-1 cursor-pointer">
                        <div className="font-medium">One-time</div>
                        <div className="text-sm text-muted-foreground">Single donation</div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-secondary/50">
                      <RadioGroupItem value="monthly" id="monthly" />
                      <Label htmlFor="monthly" className="flex-1 cursor-pointer">
                        <div className="font-medium">Monthly</div>
                        <div className="text-sm text-muted-foreground">Recurring donation</div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-secondary/50">
                      <RadioGroupItem value="annual" id="annual" />
                      <Label htmlFor="annual" className="flex-1 cursor-pointer">
                        <div className="font-medium">Annual</div>
                        <div className="text-sm text-muted-foreground">Yearly donation</div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Donor Information */}
                <div className="space-y-6">
                  <Label className="text-lg font-medium">Donor Information</Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        value={donorInfo.firstName}
                        onChange={(e) => setDonorInfo({...donorInfo, firstName: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={donorInfo.lastName}
                        onChange={(e) => setDonorInfo({...donorInfo, lastName: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={donorInfo.email}
                        onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={donorInfo.phone}
                        onChange={(e) => setDonorInfo({...donorInfo, phone: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Address (for tax receipt)</Label>
                    <Textarea
                      id="address"
                      value={donorInfo.address}
                      onChange={(e) => setDonorInfo({...donorInfo, address: e.target.value})}
                      placeholder="Street address, City, State, ZIP"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message (Optional)</Label>
                    <Textarea
                      id="message"
                      value={donorInfo.message}
                      onChange={(e) => setDonorInfo({...donorInfo, message: e.target.value})}
                      placeholder="Share why you're supporting our heritage mission..."
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="anonymous"
                      checked={donorInfo.anonymous}
                      onCheckedChange={(checked) => setDonorInfo({...donorInfo, anonymous: checked as boolean})}
                    />
                    <Label htmlFor="anonymous" className="text-sm font-normal cursor-pointer">
                      Make this donation anonymous
                    </Label>
                  </div>
                </div>

                {/* Submit Button */}
                {!showPaymentForm && (
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    {isSubmitted ? (
                      <div className="flex-1 text-center py-4">
                        <div className="flex items-center justify-center text-heritage mb-2">
                          <CheckCircle className="w-6 h-6 mr-2" />
                          <span className="text-lg font-semibold">Donation Submitted Successfully!</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Thank you for your generous contribution. You will receive a confirmation email shortly.
                        </p>
                      </div>
                    ) : (
                      <Button 
                        type="submit" 
                        variant="heritage" 
                        size="lg" 
                        className="flex-1"
                        disabled={isSubmitting || getCurrentAmount() === 0 || !donorInfo.firstName || !donorInfo.lastName || !donorInfo.email}
                      >
                        <Heart className="w-5 h-5 mr-2" />
                        {isSubmitting ? "Processing..." : `Continue to Payment${getCurrentAmount() > 0 ? ` - $${getCurrentAmount()}` : ""}`}
                        {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
                      </Button>
                    )}
                  </div>
                )}

                {!showPaymentForm && (
                  <div className="text-center text-sm text-muted-foreground pt-4">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Your donation is secure and tax-deductible. You will receive a receipt for your records.
                  </div>
                )}
              </form>

              {/* Payment Form */}
              {showPaymentForm && donationId && (
                <div className="mt-8 pt-8 border-t">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2">Complete Your Payment</h3>
                    <p className="text-sm text-muted-foreground">
                      Amount: <span className="font-semibold text-heritage">${getCurrentAmount()}</span>
                    </p>
                  </div>
                  <StripePaymentForm
                    amount={getCurrentAmount()}
                    currency="USD"
                    donationId={donationId}
                    customerEmail={donorInfo.email}
                    customerName={`${donorInfo.firstName} ${donorInfo.lastName}`}
                    description={`Donation to ${import.meta.env.VITE_ORGANIZATION_NAME || 'Organization'}`}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    metadata={{
                      donationType,
                      donorLevel: getDonorLevel(getCurrentAmount()),
                      anonymous: donorInfo.anonymous,
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setDonationId(null);
                    }}
                    className="mt-4"
                  >
                    Back to Form
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-12">
            Other Ways to Support Our Mission
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-warm hover:shadow-heritage transition-shadow">
              <CardContent className="p-8 text-center">
                <Gift className="w-12 h-12 text-heritage mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Memorial Gifts</h3>
                <p className="text-muted-foreground mb-6">
                  Honor loved ones through meaningful donations that preserve their memory in our community heritage.
                </p>
                <Button variant="outline" asChild>
                  <a href="mailto:info@ifiheritage.org">Learn More</a>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="shadow-warm hover:shadow-heritage transition-shadow">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-heritage mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Corporate Giving</h3>
                <p className="text-muted-foreground mb-6">
                  Partner with us through corporate sponsorships and employee giving programs to strengthen our community.
                </p>
                <Button variant="outline" asChild>
                  <a href="mailto:partnerships@ifiheritage.org">Partner With Us</a>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="shadow-warm hover:shadow-heritage transition-shadow">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-heritage mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Planned Giving</h3>
                <p className="text-muted-foreground mb-6">
                  Create a lasting legacy through bequests, charitable trusts, and other planned giving opportunities.
                </p>
                <Button variant="outline" asChild>
                  <a href="mailto:legacy@ifiheritage.org">Plan Your Legacy</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
